import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { loadManagementAPI, TeamLoadSummary } from '@/services/api';

interface LoadManagementWidgetProps {
  teamId?: string;
}

const LoadManagementWidget: React.FC<LoadManagementWidgetProps> = ({ teamId }) => {
  const [teamLoadData, setTeamLoadData] = useState<TeamLoadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTeamLoadData = async () => {
    try {
      setLoading(true);
      setTeamLoadData((await loadManagementAPI.getTeamLoadSummary()) ?? []);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load team ACWR data');
      setTeamLoadData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchTeamLoadData();
    // teamId is the request context trigger; the request function is intentionally local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const getRiskCategoryColor = (category: string) => {
    switch (category) {
      case 'very_high':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/60 dark:text-orange-200 dark:border-orange-800';
      case 'moderate':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-950/60 dark:text-green-200 dark:border-green-800';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/60 dark:text-blue-200 dark:border-blue-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700';
    }
  };

  const getRiskCategoryLabel = (category: string) => {
    switch (category) {
      case 'very_high':
        return 'Very High Risk';
      case 'high':
        return 'High Risk';
      case 'moderate':
        return 'Moderate Risk';
      case 'low':
        return 'Low Risk';
      case 'insufficient_data':
        return 'No Data';
      default:
        return 'Unknown';
    }
  };

  const getACWRIndicator = (acwr: number | null, category: string) => {
    if (!acwr || category === 'insufficient_data') {
      return <span className="text-gray-500 dark:text-gray-400">—</span>;
    }
    
    let color: string;
    if (category === 'very_high' || category === 'high') color = 'text-red-600 dark:text-red-400';
    else if (category === 'moderate') color = 'text-green-600 dark:text-green-400';
    else color = 'text-blue-600 dark:text-blue-400';
    
    return <span className={`font-semibold ${color}`}>{acwr.toFixed(2)}</span>;
  };

  const highRiskPlayers = teamLoadData?.filter(p => p.riskCategory === 'high' || p.riskCategory === 'very_high') || [];
  const moderateRiskPlayers = teamLoadData?.filter(p => p.riskCategory === 'moderate') || [];
  const lowRiskPlayers = teamLoadData?.filter(p => p.riskCategory === 'low') || [];

  if (loading) {
    return (
      <Card className="p-6">
        <div role="status" aria-label="Loading team load management" className="animate-pulse motion-reduce:animate-none motion-reduce:transition-none">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-4/6"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Load Management</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">ACWR-based injury risk monitoring</p>
        </div>
        <Link
          to="/players"
          className="inline-flex min-h-11 items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm font-medium transition-colors motion-reduce:transition-none"
        >
          View Details →
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 dark:bg-red-950/40 dark:border-red-800 rounded-md p-3 mb-4">
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          <Button variant="outline" size="sm" className="mt-2 min-h-11 motion-reduce:transition-none" onClick={() => void fetchTeamLoadData()}>
            Retry load management
          </Button>
        </div>
      )}

      {/* Show message when no data is available */}
      {!error && teamLoadData.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800 rounded-md p-4 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg aria-hidden="true" className="h-5 w-5 text-blue-400 dark:text-blue-300" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">Load Management Data Not Available</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                ACWR calculations require training attendance data. Please ensure training sessions and attendance are being tracked.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Risk Summary */}
      {teamLoadData.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{highRiskPlayers.length}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300">High Risk</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{moderateRiskPlayers.length}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300">Moderate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{lowRiskPlayers.length}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300">Low Risk</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{teamLoadData?.length || 0}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300">Total</div>
          </div>
        </div>
      )}

      {/* High Risk Players Alert */}
      {highRiskPlayers.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-400 dark:bg-red-950/40 dark:border-red-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg aria-hidden="true" className="h-5 w-5 text-red-400 dark:text-red-300" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                {highRiskPlayers.length} player{highRiskPlayers.length !== 1 ? 's' : ''} at high injury risk
              </h4>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {highRiskPlayers.slice(0, 3).map(p => p.name).join(', ')}
                {highRiskPlayers.length > 3 && ` and ${highRiskPlayers.length - 3} more`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent ACWR Data */}
      {teamLoadData.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Recent ACWR Values</h4>
          {teamLoadData?.slice(0, 6).map((player) => (
            <div key={player.playerId} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{player.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{player.position}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium">
                    {getACWRIndicator(player.acwr, player.riskCategory)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">ACWR</div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRiskCategoryColor(player.riskCategory)}`}>
                  {getRiskCategoryLabel(player.riskCategory)}
                </span>
              </div>
            </div>
          ))}
          
          {(teamLoadData?.length || 0) > 6 && (
            <div className="text-center pt-3">
              <Link
                to="/players"
                className="inline-flex min-h-11 items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors motion-reduce:transition-none"
              >
                View all {teamLoadData?.length || 0} players →
              </Link>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default LoadManagementWidget;
